import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router';

import { createApiClient } from '@/api/client';
import type { ApiError } from '@/api/client.types';
import { createLegacyKuduToken } from '@/api/legacyKuduAuth';
import { useAuth } from '@/auth/useAuth';
import { contentEditorQueryKey } from '@/features/content/editor/useSaveComponent';
import type { RawComponent } from '@/features/content/editor/editorTypes';
import { COURSES_QUERY_KEY } from '@/features/courses/useCourses';
import { courseQueryKey } from '@/features/courses/useCourse';

import { copySections } from './copyService';
import type {
  ContentSection,
  CopySectionPosition,
} from './sectionTypes';

export interface CopySectionMutationInput {
  position: CopySectionPosition;
  sections: ContentSection[];
  targetComponent: RawComponent;
  targetComponentId: string;
  targetCourseId: string;
}

function createDataError(message: string): ApiError {
  const error = new Error(message) as ApiError;
  error.kind = 'data';
  return error;
}

export function useCopySection() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getIdToken, user } = useAuth();

  return useMutation<RawComponent, ApiError, CopySectionMutationInput>({
    mutationFn: async (params) => {
      if (!user) {
        throw createDataError('You must be signed in to copy a section.');
      }

      if (!params.targetCourseId.trim()) {
        throw createDataError('Select a course before copying this section.');
      }

      if (!params.targetComponentId.trim()) {
        throw createDataError('Select a chapter before copying this section.');
      }

      const returnTo = `${location.pathname}${location.search}${location.hash}`;
      const client = createApiClient({
        baseUrl: import.meta.env.VITE_API_BASE_URL || '',
        getIdToken,
        getLegacyToken: async () => createLegacyKuduToken(user),
        onAuthError: () =>
          navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`, {
            replace: true,
          }),
      });

      const savedComponent = await copySections(client, {
        courseId: params.targetCourseId,
        position: params.position,
        sections: params.sections,
        targetComponent: params.targetComponent,
      });
      const targetEditorQueryKey = contentEditorQueryKey(
        params.targetCourseId,
        params.targetComponentId,
      );

      await queryClient.invalidateQueries({ queryKey: targetEditorQueryKey });

      return queryClient.getQueryData<RawComponent>(targetEditorQueryKey) ?? savedComponent;
    },
    onSuccess: async (_savedComponent, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: COURSES_QUERY_KEY }),
        queryClient.invalidateQueries({
          queryKey: courseQueryKey(variables.targetCourseId),
        }),
      ]);
    },
  });
}
